import * as React from 'react'
import moment from 'moment'

import { EventModel } from '@/app/models/newDelegation/eventModel'
import { getEventTypeIcon } from '@/app/common/converters/newDelegation/tripEventTypeToIconConverter'
import { getTripEventTypeName } from '@/app/common/converters/newDelegation/tripEventTypeToTextConverter'
import { TripEventType } from '@/app/constants/delegation/tripEventType'
import { getCountryFlag } from '@/app/services/country/countryFlagResolver'
import { FaIcon } from '@/lib/fa'

import { useScene } from '../context/SceneContext'
import { useConfig } from '../context/ConfigContext'
import { BOTTOM_PADDING } from '../utils/layout'
import { MARKER_LANE_STEP } from '../layers/pointLanes'

export type DelegationMarkerBlockProps = {
  delegationMarker: EventModel
  index: number
  lane?: number
  current?: boolean
}

const formatMarkerTime = (date: EventModel['date']): string => {
  if (!date) return ''
  const m = moment(date as any)
  return m.isValid() ? m.format('HH:mm') : ''
}

export const DelegationMarkerBlock: React.FC<DelegationMarkerBlockProps> =
  React.memo((props: DelegationMarkerBlockProps) => {
    const scene = useScene()
    const { translate, stackOverlappingMarkers, dimOtherTripMarkers } =
      useConfig()

    const marker = props.delegationMarker
    const left = scene.timeToX(marker.date) - 15
    const lane = stackOverlappingMarkers ? (props.lane ?? 0) : 0
    const glyphPointerEvents: React.CSSProperties['pointerEvents'] =
      stackOverlappingMarkers ? 'auto' : undefined

    const isDimmed = dimOtherTripMarkers && props.current === false
    const isOld = marker?.id != null
    const badgeClass = isOld
      ? 'tw-border-neutral tw-bg-neutral'
      : 'tw-border-primary tw-bg-primary'
    const labelClass = isOld ? 'tw-bg-neutral' : 'tw-bg-primary'

    const icon = getEventTypeIcon(marker.tripEvent)
    const title = getTripEventTypeName(marker.tripEvent, translate)
    const showFlag =
      marker.tripEvent === TripEventType.ChangeCountry && !!marker.country?.code

    return (
      <div
        title={title}
        style={{
          left,
          position: 'absolute',
          top: 0,
          bottom: 0,
          paddingBottom: BOTTOM_PADDING,
       
          pointerEvents: stackOverlappingMarkers ? 'none' : 'auto',
        }}
      >
        <div
          className={`tw-flex tw-h-full tw-w-[30px] tw-flex-col tw-items-center tw-justify-start ${
            isDimmed ? 'tw-grayscale' : ''
          }`}
          style={{ paddingTop: lane * MARKER_LANE_STEP }}
        >
          {showFlag ? (
            <div
              className={
                stackOverlappingMarkers
                  ? 'tw-grid tw-h-6 tw-w-6 tw-place-items-center'
                  : ''
              }
              style={{ pointerEvents: glyphPointerEvents }}
            >
              <img
                src={getCountryFlag(
                  marker.country!.code.toUpperCase() === 'GB'
                    ? 'EN'
                    : marker.country!.code,
                )}
                width={20}
                height={15}
              />
            </div>
          ) : (
            <div
              className={`tw-grid tw-h-6 tw-w-6 tw-place-items-center tw-rounded-full tw-border tw-shadow-sm ${badgeClass}`}
              style={{ pointerEvents: glyphPointerEvents }}
            >
              {icon && (
                <FaIcon className="tw-h-3 tw-w-3 tw-text-white" icon={icon} />
              )}
            </div>
          )}

          <div className="tw-w-[1px] tw-flex-1 tw-bg-primary" />

          <div
            className={`tw-grid tw-place-items-center tw-rounded tw-px-0.5 tw-py-[1px] ${labelClass}`}
            style={{ pointerEvents: glyphPointerEvents }}
          >
            <span className="tw-text-[10px] tw-font-medium tw-text-base-100">
              {formatMarkerTime(marker.date)}
            </span>
          </div>
        </div>
      </div>
    )
  })

DelegationMarkerBlock.displayName = 'DelegationMarkerBlock'
